import { useState, useEffect, useRef } from 'react'
import { supabase } from '../supabaseClient'
import { localDateString } from '../utils/date'

export default function Pod({ session, behavior }) {
  const [pod, setPod] = useState(null)
  const [members, setMembers] = useState([])
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [loading, setLoading] = useState(true)
  const [joining, setJoining] = useState(false)
  const messagesEndRef = useRef(null)
  const channelRef = useRef(null)

  useEffect(() => {
    loadPod()
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current)
        channelRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session])

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages])

  function setupChannel(podId) {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current)
    }

    const channel = supabase
      .channel(`pod-${podId}`)
      // Broadcast: no table config needed, works out of the box
      .on('broadcast', { event: 'new_message' }, ({ payload }) => {
        setMessages(prev => {
          if (prev.some(m => m.id === payload.id)) return prev
          return [...prev, payload]
        })
      })
      // Reload member list when someone new joins the pod
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'pod_members', filter: `pod_id=eq.${podId}` },
        () => { loadMemberDetails(podId) }
      )
      .subscribe()

    channelRef.current = channel
  }

  async function loadPod() {
    const { data: rows, error: memberErr } = await supabase
      .from('pod_members')
      .select('pod_id')
      .eq('user_id', session.user.id)
      .limit(1)

    console.log('[Pod] loadPod — pod_members lookup:', { rows, error: memberErr?.message })

    const memberData = rows?.[0]
    if (memberData) {
      await loadPodData(memberData.pod_id)
    }

    setLoading(false)
  }

  async function loadMemberDetails(podId) {
    const { data: membersData, error: membersErr } = await supabase
      .from('pod_members')
      .select('user_id')
      .eq('pod_id', podId)

    console.log('[Pod] loadMemberDetails — rows:', membersData?.length, 'error:', membersErr?.message)

    if (!membersData || membersData.length === 0) return

    // Deduplicate user_ids in case of any legacy duplicate rows
    const unique = [...new Map(membersData.map(m => [m.user_id, m])).values()]

    const memberDetails = await Promise.all(
      unique.map(async (m) => {
        const { data: profile } = await supabase
          .from('profiles')
          .select('username')
          .eq('id', m.user_id)
          .single()

        const { data: streak } = await supabase
          .from('streaks')
          .select('days_count')
          .eq('user_id', m.user_id)
          .eq('is_active', true)
          .single()

        const { data: checkin } = await supabase
          .from('checkins')
          .select('stayed_clean')
          .eq('user_id', m.user_id)
          .eq('date', localDateString())
          .single()

        const firstName = profile?.username?.split(' ')[0] || 'Member'

        return {
          user_id: m.user_id,
          firstName,
          streak: streak?.days_count || 0,
          checkedInToday: !!checkin,
          isMe: m.user_id === session.user.id,
        }
      })
    )
    setMembers(memberDetails)
  }

  async function loadPodData(podId) {
    const { data: podData, error: podErr } = await supabase
      .from('pods')
      .select('*')
      .eq('id', podId)
      .single()

    console.log('[Pod] loadPodData — pods lookup:', { podData, error: podErr?.message })

    if (!podData) return

    setPod(podData)
    setupChannel(podId)
    await loadMemberDetails(podId)

    const { data: messagesData, error: msgErr } = await supabase
      .from('pod_messages')
      .select('*')
      .eq('pod_id', podId)
      .order('created_at', { ascending: true })
      .limit(50)

    console.log('[Pod] loadPodData — messages:', messagesData?.length, 'error:', msgErr?.message)

    if (messagesData) setMessages(messagesData)
  }

  async function joinPod() {
    setJoining(true)

    try {
      // Guard: if the user is already in any pod, just load it — never create a second one.
      const { data: existingMembership } = await supabase
        .from('pod_members')
        .select('pod_id')
        .eq('user_id', session.user.id)
        .limit(1)

      if (existingMembership?.length > 0) {
        console.log('[Pod] joinPod — user already in a pod, loading existing')
        await loadPodData(existingMembership[0].pod_id)
        return
      }

      const { data: existingPods, error: podsErr } = await supabase
        .from('pods')
        .select('id')
        .eq('behavior', behavior)

      console.log('[Pod] joinPod — existing pods:', existingPods, 'error:', podsErr?.message)

      let podId = null

      if (existingPods && existingPods.length > 0) {
        for (const p of existingPods) {
          const { data: memberCount } = await supabase
            .from('pod_members')
            .select('user_id')
            .eq('pod_id', p.id)

          if (memberCount && memberCount.length < 5) {
            podId = p.id
            break
          }
        }
      }

      if (!podId) {
        const { data: newPod, error: podError } = await supabase
          .from('pods')
          .insert({ behavior })
          .select()
          .single()

        console.log('[Pod] joinPod — created pod:', newPod, 'error:', podError?.message)

        if (podError || !newPod) throw new Error(podError?.message || 'Failed to create pod')
        podId = newPod.id
      }

      // Only insert if not already a member of this pod
      const { data: existing } = await supabase
        .from('pod_members')
        .select('pod_id')
        .eq('pod_id', podId)
        .eq('user_id', session.user.id)
        .limit(1)

      if (!existing || existing.length === 0) {
        const { error: joinError } = await supabase
          .from('pod_members')
          .insert({ pod_id: podId, user_id: session.user.id })

        console.log('[Pod] joinPod — joined pod_members, error:', joinError?.message)

        if (joinError) throw new Error(joinError.message)
      } else {
        console.log('[Pod] joinPod — already a member, skipping insert')
      }

      await loadPodData(podId)
    } catch (err) {
      console.error('[Pod] joinPod FAILED:', err.message)
    } finally {
      setJoining(false)
    }
  }

  async function sendMessage() {
    if (!newMessage.trim() || !pod) return

    const text = newMessage.trim()
    setNewMessage('')

    const { data: sent, error } = await supabase
      .from('pod_messages')
      .insert({ pod_id: pod.id, user_id: session.user.id, message: text })
      .select()
      .single()

    console.log('[Pod] sendMessage:', { sent, error: error?.message })

    if (error || !sent) {
      setNewMessage(text)
      return
    }

    setMessages(prev => [...prev, sent])

    channelRef.current?.send({
      type: 'broadcast',
      event: 'new_message',
      payload: sent,
    })

    // Fire-and-forget push notification to other pod members
    supabase.functions.invoke('send-pod-message-notification', {
      body: { pod_id: pod.id, message: text },
    }).catch(() => {})
  }

  function handleKeyPress(e) {
    if (e.key === 'Enter') sendMessage()
  }

  if (loading) return (
    <div style={styles.loading}>⚓</div>
  )

  if (!pod) return (
    <div style={styles.container}>
      <div style={styles.inner}>
        <div style={{ textAlign: 'center', paddingTop: 60 }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>👥</div>
          <h2 style={styles.title}>Accountability Pods</h2>
          <p style={styles.sub}>
            Get matched with 3-5 people fighting the same battle.
            Check in daily. Hold each other accountable.
          </p>

          <div style={styles.featureList}>
            {[
              '🔒 Anonymous — no personal info shared',
              '📅 Daily check-ins with your group',
              '💬 Group chat for support',
              '🔔 Alerts when someone needs help',
            ].map((f, i) => (
              <div key={i} style={styles.feature}>
                <p style={styles.featureText}>{f}</p>
              </div>
            ))}
          </div>

          <button
            style={styles.joinBtn}
            onClick={joinPod}
            disabled={joining}
          >
            {joining ? 'Finding your pod...' : 'Join a Pod'}
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <div style={styles.container}>
      <div style={styles.inner}>

        <div style={styles.header}>
          <h2 style={styles.title}>My Pod</h2>
          <span style={styles.behaviorTag}>{behavior}</span>
        </div>

        <div style={styles.membersCard}>
          <p style={styles.sectionLabel}>Pod Members — {members.length}/5</p>
          {members.map((m, i) => (
            <div key={m.user_id} style={styles.memberRow}>
              <div style={{
                ...styles.avatar,
                background: m.isMe
                  ? 'linear-gradient(135deg, #7c3aed, #2563eb)'
                  : `hsl(${i * 60 + 180}, 50%, 40%)`
              }}>
                {m.firstName[0].toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <p style={styles.memberName}>
                  {m.firstName}{m.isMe ? ' (You)' : ''}
                </p>
                <p style={styles.memberStreak}>
                  🔥 {m.streak} day streak
                </p>
              </div>
              <div style={{
                ...styles.checkinDot,
                background: m.checkedInToday
                  ? '#4ade80'
                  : 'rgba(255,255,255,0.15)'
              }} />
            </div>
          ))}
        </div>

        <div style={styles.chatCard}>
          <p style={styles.sectionLabel}>Pod Chat</p>
          <div style={styles.messages}>
            {messages.length === 0 && (
              <p style={styles.emptyChat}>
                No messages yet. Say hello to your pod! 👋
              </p>
            )}
            {(() => {
              const memberMap = Object.fromEntries(members.map(m => [m.user_id, m.firstName]))
              return messages.map((msg, i) => {
                const isMe = msg.user_id === session.user.id
                const senderName = isMe ? 'You' : (memberMap[msg.user_id] || 'Member')
                return (
                  <div
                    key={msg.id || i}
                    style={{ alignSelf: isMe ? 'flex-end' : 'flex-start', maxWidth: '75%' }}
                  >
                    <p style={{
                      ...styles.messageSender,
                      textAlign: isMe ? 'right' : 'left',
                    }}>
                      {senderName}
                    </p>
                    <div style={{
                      ...styles.message,
                      background: isMe
                        ? 'linear-gradient(135deg, #7c3aed, #2563eb)'
                        : 'rgba(255,255,255,0.08)',
                    }}>
                      <p style={styles.messageText}>{msg.message}</p>
                    </div>
                  </div>
                )
              })
            })()}
            <div ref={messagesEndRef} />
          </div>
          <div style={styles.inputRow}>
            <input
              style={styles.input}
              placeholder="Send a message..."
              value={newMessage}
              onChange={e => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <button style={styles.sendBtn} onClick={sendMessage}>
              Send
            </button>
          </div>
        </div>

      </div>
    </div>
  )
}

const styles = {
  loading: {
    minHeight: '100vh',
    background: '#080812',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 48,
  },
  container: {
    minHeight: '100vh',
    background: '#080812',
    display: 'flex',
    justifyContent: 'center',
    padding: '40px 20px 100px',
  },
  inner: {
    width: '100%',
    maxWidth: 400,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  title: {
    color: '#fff',
    fontFamily: 'serif',
    fontSize: 24,
    margin: 0,
  },
  sub: {
    color: 'rgba(255,255,255,0.45)',
    fontSize: 14,
    lineHeight: 1.6,
    marginBottom: 32,
  },
  behaviorTag: {
    background: 'rgba(124,58,237,0.2)',
    border: '1px solid rgba(124,58,237,0.3)',
    color: '#a78bfa',
    fontSize: 12,
    padding: '4px 12px',
    borderRadius: 20,
    textTransform: 'capitalize',
  },
  featureList: {
    marginBottom: 32,
    textAlign: 'left',
  },
  feature: {
    padding: '10px 0',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  featureText: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 14,
    margin: 0,
  },
  joinBtn: {
    width: '100%',
    padding: 16,
    background: 'linear-gradient(135deg, #7c3aed, #2563eb)',
    border: 'none',
    borderRadius: 14,
    color: '#fff',
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
  },
  sectionLabel: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: 11,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    margin: '0 0 16px',
  },
  membersCard: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 20,
    padding: 20,
    marginBottom: 16,
  },
  memberRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: 11,
    fontWeight: 700,
    flexShrink: 0,
  },
  memberName: {
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    margin: 0,
  },
  memberStreak: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: 12,
    margin: 0,
  },
  checkinDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    flexShrink: 0,
  },
  chatCard: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 20,
    padding: 20,
  },
  messages: {
    minHeight: 200,
    maxHeight: 300,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginBottom: 16,
  },
  emptyChat: {
    color: 'rgba(255,255,255,0.3)',
    fontSize: 13,
    textAlign: 'center',
    margin: 'auto',
    paddingTop: 60,
  },
  messageSender: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: 11,
    fontWeight: 600,
    margin: '0 0 3px 2px',
    letterSpacing: 0.3,
  },
  message: {
    padding: '8px 14px',
    borderRadius: 16,
  },
  messageText: {
    color: '#fff',
    fontSize: 14,
    margin: 0,
  },
  inputRow: {
    display: 'flex',
    gap: 8,
  },
  input: {
    flex: 1,
    padding: '12px 14px',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 12,
    color: '#fff',
    fontSize: 14,
    outline: 'none',
  },
  sendBtn: {
    padding: '12px 16px',
    background: 'linear-gradient(135deg, #7c3aed, #2563eb)',
    border: 'none',
    borderRadius: 12,
    color: '#fff',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
  },
}
